import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit, Trash2, Building2, ExternalLink } from "lucide-react";

const empty = { name: "", logo_url: "", website: "", contact_email: "", contact_phone: "", description: "", category: "serveis", active: true };
const CATS = { assegurances: "Assegurances", gestories: "Gestories", formacio: "Formació", telecom: "Telecom", proveidors: "Proveïdors", serveis: "Serveis professionals", altres: "Altres" };

export default function AdminCollaborators() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = async () => setItems((await api.get("/collaborators")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nom obligatori");
    try {
      if (editing) await api.put(`/collaborators/${editing.id}`, form);
      else await api.post("/collaborators", form);
      toast.success(editing ? "Actualitzat" : "Creat");
      setOpen(false); setForm(empty); setEditing(null); load();
    } catch { toast.error("Error"); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Eliminar ${c.name}?`)) return;
    await api.delete(`/collaborators/${c.id}`); load();
  };
  const edit = (c) => { setEditing(c); setForm({ ...empty, ...c }); setOpen(true); };

  return (
    <div className="space-y-6" data-testid="admin-collaborators">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-accent">Empreses externes</div>
          <h1 className="mt-2 text-3xl font-heading font-bold tracking-tight">Col·laboradors</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Assegurances, gestories, formació i altres proveïdors amb els quals Shopping Salou ha arribat a acords per als associats.
          </p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty); setEditing(null); } }}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="collab-new-btn"><Plus className="w-4 h-4 mr-2" /> Nou col·laborador</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Editar" : "Nou"} col·laborador</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nom *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="collab-name" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Logo URL</Label><Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://…" /></div>
                <div><Label>Web</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" /></div>
                <div><Label>Email contacte</Label><Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
                <div><Label>Telèfon</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></div>
              </div>
              <div><Label>Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="collab-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CATS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Descripció</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Serveis i avantatges que ofereix als associats..." /></div>
              <div className="flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> Actiu</div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="collab-save-btn">Desar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map(c => (
          <Card key={c.id} className={`rounded-2xl border-2 ${c.active ? "" : "opacity-60"}`} data-testid={`collab-row-${c.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  {c.logo_url ? <img src={c.logo_url} alt="" className="w-10 h-10 object-contain" /> : <Building2 className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-semibold truncate">{c.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-accent">{CATS[c.category] || c.category}</div>
                </div>
              </div>
              {c.description && <p className="text-xs text-muted-foreground mt-3 line-clamp-2">{c.description}</p>}
              <div className="flex gap-2 mt-3 pt-3 border-t items-center">
                {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Web</a>}
                <div className="ml-auto flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => edit(c)} data-testid={`collab-edit-${c.id}`}><Edit className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="text-destructive" onClick={() => remove(c)} data-testid={`collab-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <div className="col-span-full text-center py-16 text-muted-foreground">Cap col·laborador afegit encara.</div>}
      </div>
    </div>
  );
}
